import { router } from "../src/router";
import { updateCurrentYear } from "../src/utils/updateCurrentYear";
import { AppHeaderNav } from "../src/components/app-layout/AppHeaderNav";
import { Sidebar } from "../src/components/app-layout/Sidebar";
import { AppFooter } from "../src/components/app-layout/AppFooter";
import { UpgradePlanModal } from "../src/components/workspaces/UpgradePlanModal";
import { workspaceService } from "../src/services/workspaceService";
import { CreateWorkspaceModal } from "../src/components/workspaces/CreateWorkspaceModal";
import { workspaceStore } from "../src/stores/workspaceStore";
import { SelectedWorkspaceCtx } from "../src/contexts/SelectedWorkspaceCtx";
import { MyAccountModal } from "../src/components/my-account/MyAccountModal";
import { HSStaticMethods } from "preline";
import { userDetailStore } from "../src/stores/userDetailStore";
import { userDetailService } from "../src/services/userDetailService";
import { UserDetailCtx } from "../src/contexts/UserDetailCtx";
import { ImportsModal } from "../src/components/imports/ImportsModal";
import { loadScript } from "../src/utils/loadExternalScript";
import { ImportsModalCtx } from "../src/components/imports/ImportsModalCtx";
import { PaymentModal } from "../src/components/app-layout/PaymentModal";
import { PaymentCtx } from "../src/contexts/PaymentCtx";
import { VideoTutorialModal } from "../src/utils/video-tutorial-modal";

function initAppLayout() {
  loadScript(`${import.meta.env.VITE_CORE_URL}`);
  loadScript(`${import.meta.env.VITE_CORE_URL}embed/embed.min.js`);
  updateCurrentYear();
}

export function AppLayout() {
  const appHeaderNav = AppHeaderNav();
  const createWorkspaceModal = CreateWorkspaceModal();
  const upgradePlanModal = UpgradePlanModal();
  const appSidebar = Sidebar({ createWSModalOpen: createWorkspaceModal.open, upgradeModalOpen: upgradePlanModal.open });
  const videoTutorialModal = VideoTutorialModal();
  const appFooter = AppFooter();
  const paymentModal = PaymentModal();

  const myAccountModal = MyAccountModal();

  const children = { appHeaderNav, createWorkspaceModal, upgradePlanModal, appSidebar, appFooter, paymentModal,videoTutorialModal };
  
  ImportsModalCtx.setImportsModal(ImportsModal()); // save the Imports Modal instance to ctx

  // render and init the Imports Modal if #import present in the URL
  function injectImportsModalConditionally() {
    const url = new URL(window.location.href);
    const hash = url.hash;

    if (hash.startsWith("#import")) {
      if (!ImportsModalCtx.rendered && ImportsModalCtx.importsModal) {
        ImportsModalCtx.addImportsModalDOM(); // render the Imports Modal into the DOM
      }
    }
  }

  let workspaceStoreUnsubscribe: () => void;
  let userDetailStoreUnsubscribe: () => void;

  let $layoutContentContainer: HTMLElement | null = null;

  function init() {
    $layoutContentContainer = document.querySelector("#layout-content");
    const currentPath = router.getCurrentLocation();
    children.appSidebar.updateSidebarActiveItem(`/${currentPath.url}`);
    HSStaticMethods.autoInit();
    initAppLayout();

    myAccountModal.initDynamically(); // Dynamic Initialization for the Dynamically Loaded Profile Modal
    window.addEventListener('popstate', myAccountModal.handleURLParameters); // watch the URL state for Profile Modal

    if (!workspaceStore.firstFetch && !workspaceStore.loading) {
      // if workspaces first fetch is not started or loaded, then make a request to fetch all workspaces
      workspaceService.list();
    }

    workspaceStoreUnsubscribe = workspaceStore.subscribe(({ workspaces }) => {
      if (workspaces && workspaces.length > 0) {
        SelectedWorkspaceCtx.loadSelectedWorkspace(workspaces);
      }
    });

    if (!userDetailStore.firstFetch && !userDetailStore.loading) {
      // if user details first fetch is not started or loaded, then make a request to fetch user details
      userDetailService.fetchDetail();
    }

    userDetailStoreUnsubscribe = userDetailStore.subscribe(({ userDetail }) => {
      if (userDetail && Object.entries(userDetail).length > 0) {
        UserDetailCtx.setUserDetail(userDetail);
      }
    });

    window.addEventListener("popstate", injectImportsModalConditionally);
    injectImportsModalConditionally();

    PaymentCtx.paymentModal = children.paymentModal;
  }

  function reinit() {
    const currentPath = router.getCurrentLocation();
    children.appSidebar.updateSidebarActiveItem(`/${currentPath.url}`);

    if (!workspaceStore.firstFetch && !workspaceStore.loading) {
      // if workspaces first fetch is not started or loaded, then make a request to fetch all workspaces
      workspaceService.list();
    }
  }

  function populate(content: string) {
    if ($layoutContentContainer)
      $layoutContentContainer.innerHTML = content;
    else
      console.error("App layout content container not found!");
  }

  function cleanUp() {
    if (workspaceStoreUnsubscribe) {
      workspaceStoreUnsubscribe();
    }

    if (userDetailStoreUnsubscribe) {
      userDetailStoreUnsubscribe();
    }

    window.removeEventListener("popstate", injectImportsModalConditionally);
    window.removeEventListener('popstate', myAccountModal.handleURLParameters);
  }

  function render() {
    return /*html*/`
      ${children.appHeaderNav.render()}
      ${children.appSidebar.render()}
      ${children.videoTutorialModal.render()}

      <!-- Main Content -->
      <main id="content" class="lg:ps-[260px] pt-[59px]">
        <div class="p-2 sm:p-5 md:pt-5 space-y-5">
          <div id="layout-content" class="min-h-[calc(100vh-115px)] sm:min-h-[calc(100vh-163px)]"></div>
        </div>
      </main>
      <!-- END Main Content -->

      ${children.appFooter.render()}
      ${children.createWorkspaceModal.render()}
      ${children.upgradePlanModal.render()}
      ${children.paymentModal.render()}
    `;
  }

  return { init, reinit, render, populate, children, cleanUp };
}